/**
 * AML Status Display Component
 * 
 * Shows the current AML compliance status for a seller onboarding
 */

import React, { useEffect, useState } from "react";
import { CheckCircle, Clock, AlertCircle, XCircle } from "lucide-react";

const STATUS_CONFIG = {
  PENDING_UPLOAD: {
    label: "Pending Upload",
    color: "bg-amber-500/10 border-amber-500/30",
    textColor: "text-amber-300",
    icon: Clock,
    description: "Awaiting document upload"
  },
  UNDER_REVIEW: {
    label: "Under Review",
    color: "bg-blue-500/10 border-blue-500/30",
    textColor: "text-blue-300",
    icon: Clock,
    description: "Your documents are being verified"
  },
  APPROVED: {
    label: "Approved",
    color: "bg-emerald-500/10 border-emerald-500/30",
    textColor: "text-emerald-300",
    icon: CheckCircle,
    description: "AML verification complete"
  },
  REJECTED: {
    label: "Rejected",
    color: "bg-red-500/10 border-red-500/30",
    textColor: "text-red-400",
    icon: XCircle,
    description: "Please contact support"
  },
  EXPIRED: {
    label: "Expired",
    color: "bg-red-500/10 border-red-500/30",
    textColor: "text-red-400",
    icon: AlertCircle,
    description: "Document verification has expired"
  }
};

export function AMLStatusDisplay({
  onboardingId,
  className,
  showDetails = false
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(
          `/api/seller/onboarding/${onboardingId}/aml-status`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch AML status");
        }

        const data = await response.json();
        setStatus(data.data);
      } catch (err) {
        setError(err?.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [onboardingId]);

  if (loading) {
    return (
      <div className={`${className} bg-white/5 border border-white/10 rounded-xl p-4`}>
        <div className="flex items-center gap-3">
          <div className="w-4 h-4 bg-white/20 rounded-full animate-pulse" />
          <span className="text-sm text-white/60">Loading AML status...</span>
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className={`${className} bg-red-500/10 border border-red-500/30 rounded-xl p-4`}>
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-sm text-red-300">Error loading AML status</span>
        </div>
      </div>
    );
  }

  const config = STATUS_CONFIG[status.amlStatus];
  if (!config) {
    return null;
  }

  const IconComponent = config.icon;

  return (
    <div className={`${className} ${config.color} border rounded-xl p-4 transition`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <IconComponent className={`w-5 h-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={`font-semibold ${config.textColor}`}>{config.label}</p>
            <p className="text-xs text-white/60 mt-1">{config.description}</p>
          </div>
        </div>

        {status.amlApprovedAt && (
          <span className="text-xs text-white/60">
            {new Date(status.amlApprovedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Details */}
      {showDetails && status.documentValidations.length > 0 && (
        <div className="mt-4 space-y-2 pt-4 border-t border-white/10">
          <p className="text-xs font-medium text-white/70">Document Validation Results</p>
          {status.documentValidations.map((validation, idx) => (
            <div key={idx} className="text-xs text-white/60 flex items-start gap-2">
              <span className="mt-0.5">
                {validation.validationStatus === "APPROVED" ? "✓" : "○"}
              </span>
              <span>
                Doc {validation.documentId.substring(0, 8)}... — {validation.validationStatus}
                {validation.message && `: ${validation.message}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Action Message */}
      {status.amlStatus === "PENDING_UPLOAD" && (
        <p className="text-xs text-white/70 mt-3">
          Upload your documents to begin the verification process.
        </p>
      )}

      {status.amlStatus === "REJECTED" && (
        <p className="text-xs text-red-300 mt-3">
          Your documents were rejected. Please contact our support team to resubmit or discuss options.
        </p>
      )}

      {status.amlStatus === "APPROVED" && (
        <p className="text-xs text-emerald-300 mt-3">
          Your account is ready for activation.
        </p>
      )}
    </div>
  );
}
